import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Avatar, Button, Dropdown, Menu, Typography } from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  DownOutlined,
  StarOutlined,
  BookOutlined,
  FileTextOutlined,
  IdcardOutlined,
  ReadOutlined,
  MenuOutlined,
  HomeOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import logo from "../assets/images/logo.svg";
import { Header } from "antd/es/layout/layout";
import { RoutePath, RouteKey, userType } from "../global/enums.ts";
import ConfirmationModal from "../components/DeleteConfirmation.tsx";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../src/redux/store.ts";
import { signOut } from "aws-amplify/auth";
import { clearUserData, setUserLoggedOut } from "../redux/authSlice.ts";
import { clearUserDetails } from "../redux/userSlice.ts";
import Authentication from "../pages/Authentication/Authentication.tsx";
import { GratuationIcon } from "../components/customIcons.tsx";

const { Text } = Typography;

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isModalVisible, setModalVisible] = useState(false);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const userData = useSelector((state: RootState) => state.auth.userData);
  const loggedUserDetails = useSelector(
    (state: RootState) => state.user.userDetails
  );
  const [userName, setUserName] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isAdminPage, setIsAdminPage] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const storedLoginStatus = localStorage.getItem("isLoggedIn");

  // Map routes to menu keys
  const routeToKeyMap: Record<RoutePath, RouteKey> = {
    [RoutePath.Home]: RouteKey.Home,
    [RoutePath.KnowledgeHub]: RouteKey.KnowledgeHub,
    [RoutePath.QuestionForm]: RouteKey.QuestionForm,
    [RoutePath.Topic]: RouteKey.Topic,
    [RoutePath.UserManagement]: RouteKey.UserManagement,
    [RoutePath.Assets]: RouteKey.Assets,
    [RoutePath.AssessmentQuestion]: RouteKey.AssessmentQuestion,
    [RoutePath.AssessmentSummary]: RouteKey.AssessmentQuestion,
    [RoutePath.AssessmentById]: RouteKey.Home,
    [RoutePath.AssessmentManagement]: RouteKey.AssessmentManagement,
    [RoutePath.AssetCategory]: RouteKey.AssetCategory,
  };

  // Helper function to check if user is in group
  const isUserAdmin = () =>
    String(loggedUserDetails?.UserType) === userType.Admin;

  // Update selectedKeys based on the current route
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const currentKey = getRouteKey(location.pathname);
    if (currentKey) {
      setSelectedKeys([currentKey]);
    } else {
      setSelectedKeys([]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/assessments")
      if (!isLoggedIn) {
        if (storedLoginStatus === "false") {
          setShowAuthModal(true);
        }
      }
  }, [location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkSessionExpiry();
    }, 60000); // Check every minute

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  useEffect(() => {
    setIsAdminPage(
      location.pathname === "/admin" || location.pathname === "/otp"
    );

    if (userData?.signInDetails?.loginId) {
      if (isUserAdmin()) {
        setUserName("Admin");
      } else {
        setUserName(
          loggedUserDetails?.FirstName?.charAt(0).toUpperCase() +
            loggedUserDetails?.FirstName?.slice(1)
        );
      }
    }
  }, [userData, location.pathname, loggedUserDetails]);

  const getRouteKey = (path: string): RouteKey => {
    // Check if the path matches the dynamic route pattern `/assessment/:assessmentId`
    if (path.startsWith("/assessment/") && path.split("/").length === 3) {
      return RouteKey.AssessmentQuestion;
    }

    // Fallback to the static route mapping
    const staticRouteKey = Object.values(RoutePath).find(
      (route) => route === path
    );
    return staticRouteKey ? routeToKeyMap[staticRouteKey] : null; // Default to Home if not found
  };

  const handleAssessmentsClick = () => {
    // Navigate to the assessments page
    navigate("/assessments");

    if (!isLoggedIn) {
      if (storedLoginStatus === "false") {
        setShowAuthModal(true);
      }
    }
  };

  const menu = (
    <Menu
      items={[
        {
          key: "account",
          label: "Account",
          icon: <UserOutlined />,
          onClick: () => navigate("/my-account"),
        },
        {
          key: "logout",
          label: "Logout",
          icon: <LogoutOutlined />,
          onClick: () => handleDelete(),
        },
      ]}
    />
  );

  const handleDelete = () => {
    setModalVisible(true);
  };

  const checkSessionExpiry = () => {
    const sessionStartTime = localStorage.getItem("sessionStartTime");
    if (sessionStartTime) {
      const currentTime = new Date().getTime();
      const sessionDuration = currentTime - parseInt(sessionStartTime);
      const sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      if (sessionDuration > sessionTimeout) {
        // Session expired, log out the user
        handleAutoLogout();
      }
    }
  };

  const handleAutoLogout = async () => {
    try {
      await signOut();
      dispatch(clearUserData());
      dispatch(clearUserDetails());
      dispatch(setUserLoggedOut());
      localStorage.removeItem("sessionStartTime");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error during auto logout:", error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setConfirmLoading(true);
      await signOut();
      dispatch(clearUserData());
      dispatch(clearUserDetails());
      dispatch(setUserLoggedOut());
      localStorage.removeItem("sessionStartTime");
      setModalVisible(false);
      navigate("/", { replace: true });
      setConfirmLoading(false);
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleDeleteCancel = () => {
    setModalVisible(false);
  };
  const handleAuthModalonClose = () => {
    setShowAuthModal(false);
    navigate("/");
  };
  const handleAuthModalonCloseWithoutNav = () => {
    setShowAuthModal(false);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991);
  window.addEventListener("resize", () => setIsMobile(window.innerWidth < 991));

  const menuItems = (
    <Menu
      theme="light"
      className="vm__sidebar__menu vm__justify-content-end vm__h-100 vm__min-w-0"
      mode="horizontal"
      selectedKeys={selectedKeys} // Set selectedKeys dynamically
    >
      {!isAdminPage && (
        <Menu.Item
          key="0"
          icon={<HomeOutlined />}
          className="vm__sidebar__menu-item"
        >
          <Link to="https://vitromed.com/">Home</Link>
        </Menu.Item>
      )}
      <Menu.Item
        key="1"
        icon={<StarOutlined />}
        className="vm__sidebar__menu-item"
      >
        <Link to="/">Highlights</Link>
      </Menu.Item>
      <Menu.Item
        key="2"
        icon={<ReadOutlined />}
        className="vm__sidebar__menu-item"
      >
        <Link to="/knowledgehub">Knowledge Hub</Link>
      </Menu.Item>

      {isLoggedIn && isUserAdmin() && (
        <Menu.Item
          key="3"
          icon={<FileTextOutlined />}
          className="vm__sidebar__menu-item"
        >
          <Link to="/questions">Questions</Link>
        </Menu.Item>
      )}
      {isLoggedIn && isUserAdmin() && (
        <Menu.Item key="4" icon={<BookOutlined />} className="">
          <Link to="/topics">Topics</Link>
        </Menu.Item>
      )}
      {isLoggedIn && isUserAdmin() && (
        <Menu.Item
          key="8"
          icon={<GratuationIcon />}
          className="vm__sidebar__menu-item"
        >
          <Link to="/assessment-management">&nbsp;&nbsp;ART Training Hub</Link>
        </Menu.Item>
      )}
      {isLoggedIn && isUserAdmin() && (
        <Menu.Item
          key="5"
          icon={<UserOutlined />}
          className="vm__sidebar__menu-item"
        >
          <Link to="/user-management">Users</Link>
        </Menu.Item>
      )}
      {isLoggedIn && isUserAdmin() && (
        <Menu.Item
          key="9"
          icon={<AppstoreOutlined />}
          className="vm__sidebar__menu-item"
        >
          <Link to="/asset-category">Asset Category</Link>
        </Menu.Item>
      )}

      {isLoggedIn && isUserAdmin() && (
        <Menu.Item
          key="6"
          icon={<IdcardOutlined />}
          className="vm__sidebar__menu-item"
        >
          <Link to="/assets">Assets</Link>
        </Menu.Item>
      )}

      {isLoggedIn ? (
        (loggedUserDetails.UserType === 0 ||
          loggedUserDetails.UserType === 2) && (
          <Menu.Item
            key="7"
            icon={<GratuationIcon />}
            className="vm__sidebar__menu-item"
          >
            <Link to="/assessments">&nbsp;&nbsp;ART Training Hub</Link>
          </Menu.Item>
        )
      ) : (
        <Menu.Item
          key="7"
          icon={<GratuationIcon />}
          className="vm__sidebar__menu-item"
          onClick={handleAssessmentsClick}
        >
          ART Training Hub
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      {!isAdminPage && (
        <Header
          style={{
            display: "flex",
            alignItems: "center",
            height: 90,
            backgroundColor: "#fff",
          }}
          className="vm__px-0"
        >
          <div className="vm__container-fluid vm__px-2">
            <div className="vm__header vm__d-flex">
              <div className="vm__sidebar__logo">
                <Link
                  className="vm__h-100 vm__d-flex vm__flex-column vm__align-items-center vm__justify-content-center"
                  to="/"
                >
                  <img src={logo} alt="Vitromed Logo" />
                </Link>
              </div>

              {!isAdminPage && (
                <>
                  {!isMobile ? (
                    <Menu
                      theme="light"
                      className="vm__sidebar__menu vm__justify-content-end vm__h-100 vm__min-w-0"
                      mode="horizontal"
                      selectedKeys={selectedKeys} // Set selectedKeys dynamically
                    >
                      {menuItems.props.children}
                    </Menu>
                  ) : (
                    <Dropdown overlay={menuItems} trigger={["click"]}>
                      <Button
                        className="vm__menu-sm"
                        icon={<MenuOutlined />}
                        size="large"
                      />
                    </Dropdown>
                  )}
                </>
              )}

              <div className="vm__fixed-right vm__d-flex vm__gap-3">
                {isLoggedIn ? (
                  <div className="vm__inline-block vm__h-100 ">
                    <Dropdown
                      overlay={menu}
                      trigger={["click"]}
                      placement="bottomRight"
                      className="vm__h-100"
                    >
                      <div className="profile-dropdown">
                        <Avatar
                          size="large"
                          icon={<UserOutlined />}
                          className="profile-avatar"
                        />
                        <div className="profile-info">
                          <Text className="profile-name">{userName}</Text>
                          {/* <Text className="profile-email">{userEmail}</Text> */}
                        </div>
                        <DownOutlined className="profile-chevron" />
                      </div>
                    </Dropdown>
                  </div>
                ) : (
                  <div className="vm__inline-block vm__h-100 "></div>
                )}
              </div>
            </div>
          </div>
        </Header>
      )}

      {showAuthModal && (
        <Authentication
          onClosewithoutNav={() => handleAuthModalonCloseWithoutNav()}
          onClose={() => handleAuthModalonClose()}
        />
      )}

      <ConfirmationModal
        isVisible={isModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Confirm Logout"
        content="Are you sure you want to logout?"
        okText="Logout"
        confirmLoading={confirmLoading}
      />
    </>
  );
};

export default AppHeader;
